'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * True once the element has come within `rootMargin` of the viewport, and
 * true for good after that (a chart that has drawn never unmounts on scroll).
 *
 * Perf budget (2026-11-04): the Insight charts sit under the first-screen
 * photograph, and liveline (~61 KB minified) is only needed when one is about
 * to be seen. Mounting on approach keeps liveline's chunk out of the page-load
 * Script requests. Where IntersectionObserver is missing it reports true at
 * once, so the chart always draws.
 */
export function useNearViewport<T extends Element>(rootMargin = '200px'): [(node: T | null) => void, boolean] {
  const [near, setNear] = useState(false)
  const [node, setNode] = useState<T | null>(null)
  const nearRef = useRef(false)
  const ref = useCallback((el: T | null) => setNode(el), [])

  useEffect(() => {
    if (nearRef.current || !node) return
    if (typeof IntersectionObserver === 'undefined') {
      nearRef.current = true
      setNear(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          nearRef.current = true
          setNear(true)
          io.disconnect()
        }
      },
      { rootMargin },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [node, rootMargin])

  return [ref, near]
}
